/**
 * Scope Slice
 * Manages scopes (tracking areas) and locations within projects.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as scopeApi from '../../services/scopeApi';

const initialState = {
  scopesByProject: {},
  locationsByScope: {},
  loading: false,
  error: null,
};

// Async thunks
export const fetchScopes = createAsyncThunk(
  'scope/fetchByProject',
  async (projectId, { rejectWithValue }) => {
    try {
      const result = await scopeApi.fetchScopes(projectId);
      return { projectId, items: result.items || [] };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createScopeAction = createAsyncThunk(
  'scope/create',
  async ({ projectId, data }, { rejectWithValue }) => {
    try {
      const result = await scopeApi.createScope(projectId, data);
      return { projectId, item: result.item };
    } catch (error) {
      return rejectWithValue(error.message || error.data?.error || 'Failed to create scope');
    }
  }
);

export const fetchLocations = createAsyncThunk(
  'scope/fetchLocations',
  async ({ projectId, scopeId }, { rejectWithValue }) => {
    try {
      const result = await scopeApi.fetchLocations(projectId, scopeId);
      return { scopeId, items: result.items || [] };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createLocationAction = createAsyncThunk(
  'scope/createLocation',
  async ({ projectId, scopeId, data }, { rejectWithValue }) => {
    try {
      const result = await scopeApi.createLocation(projectId, scopeId, data);
      return { scopeId, item: result.item };
    } catch (error) {
      return rejectWithValue(error.message || error.data?.error || 'Failed to create location');
    }
  }
);

const scopeSlice = createSlice({
  name: 'scope',
  initialState,
  reducers: {
    clearScopesForProject: (state, action) => {
      const projectId = action.payload;
      delete state.scopesByProject[projectId];
    },
    clearLocationsForScope: (state, action) => {
      const scopeId = action.payload;
      delete state.locationsByScope[scopeId];
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch scopes
      .addCase(fetchScopes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchScopes.fulfilled, (state, action) => {
        state.loading = false;
        state.scopesByProject[action.payload.projectId] = action.payload.items;
      })
      .addCase(fetchScopes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create scope
      .addCase(createScopeAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createScopeAction.fulfilled, (state, action) => {
        state.loading = false;
        const { projectId, item } = action.payload;
        if (!state.scopesByProject[projectId]) {
          state.scopesByProject[projectId] = [];
        }
        state.scopesByProject[projectId].push(item);
      })
      .addCase(createScopeAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch locations
      .addCase(fetchLocations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLocations.fulfilled, (state, action) => {
        state.loading = false;
        state.locationsByScope[action.payload.scopeId] = action.payload.items;
      })
      .addCase(fetchLocations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create location
      .addCase(createLocationAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLocationAction.fulfilled, (state, action) => {
        state.loading = false;
        const { scopeId, item } = action.payload;
        if (!state.locationsByScope[scopeId]) {
          state.locationsByScope[scopeId] = [];
        }
        state.locationsByScope[scopeId].push(item);
      })
      .addCase(createLocationAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearScopesForProject,
  clearLocationsForScope,
  clearError,
} = scopeSlice.actions;

export default scopeSlice.reducer;
